import { Control, Controller } from 'react-hook-form'
import {
  Box,
  Button,
  DataCell,
  DateIndicator,
  Flex,
  FormField,
  Stack,
  Text,
  TextInput,
  useMediaQuery
} from '@viveaesthetics/shared'
import { addHours, format } from 'date-fns'

import { useEmailAnalytics } from '@/hooks/analytics/useEmailAnalytics'

import { ProviderBookButtonForm } from '../ProviderBookButton.types'

interface ProviderBookButtonProps {
  provider: {
    id: string
    name: string
    imgSrc: string
  }
  control: Control<ProviderBookButtonForm>
  onSubmit: () => void
  timeSlot?: Date
  isLoading?: boolean
  error?: string
}

export const EmailStep = ({
  provider,
  timeSlot,
  control,
  error,
  onSubmit,
  isLoading
}: ProviderBookButtonProps) => {
  const { isMobile } = useMediaQuery()
  const subText = timeSlot
    ? format(timeSlot, 'h:mm a') + format(addHours(timeSlot, 1), ' - h:mm a')
    : ''

  const { setEmailAnalytics } = useEmailAnalytics(timeSlot)

  return (
    <Flex
      fullHeight
      direction={isMobile ? 'column' : 'row'}
      template={isMobile ? [2, 3] : [2, 3]}
      $space="spacing0"
    >
      <Box
        fullHeight
        $padding="spacing24"
      >
        <Flex
          fullHeight
          align="flex-start"
          direction="column"
          $space="spacing20"
          justify="space-between"
          template={isMobile ? undefined : [1, 'none']}
        >
          <Box
            $padding={[
              isMobile ? 'spacing48' : 'spacing72',
              'spacing0',
              'spacing0',
              'spacing0'
            ]}
          >
            <Stack $space="spacing12">
              <Text type="title2">Your email</Text>
              <Text type="paragraphS">
                Enter your email so we can send you a link to join your session
              </Text>
            </Stack>
          </Box>

          <DataCell
            leftComponent={
              <DateIndicator
                direction="vertical"
                date={timeSlot ? new Date(timeSlot) : new Date()}
                format="MMM"
              />
            }
            text={provider.name}
            textType="subtitle1"
            subText={subText}
          />
        </Flex>
      </Box>
      <Box
        fullHeight
        $backgroundColor="cardBgDefault"
        $padding={isMobile ? ['spacing24', 'spacing16'] : 'spacing24'}
      >
        <Flex
          direction="column"
          justify="space-between"
          fullHeight
        >
          <Box
            $padding={[
              isMobile ? 'spacing0' : 'spacing72',
              'spacing0',
              'spacing0'
            ]}
          >
            <Controller
              control={control}
              name="email"
              render={({ onChange, value }) => (
                <FormField
                  label="Email"
                  text={error || ''}
                  required
                >
                  <TextInput
                    variant="secondary"
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    data-test-id="email-input"
                    onChange={onChange}
                    onBlur={() => {
                      setEmailAnalytics({
                        email: value,
                        providerId: provider.id
                      })
                    }}
                    value={value}
                    error={!!error}
                  />
                </FormField>
              )}
            />
          </Box>
          <Flex justify="flex-end">
            <Button
              fullWidth={isMobile}
              data-test-id="book-session-continue"
              onClick={onSubmit}
              loading={isLoading}
            >
              Continue
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  )
}
